import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { usersApi } from "@/api/users";
import type { AssignableUserDto } from "@/api/users";

/**
 * Shared assignable-users lookup. ONE unfiltered GET /api/users/assignable
 * (cached 10 min under a single queryKey) with role filtering applied
 * client-side from the DTO's roleCodes — so four pickers with four different
 * `?roles=` CSVs no longer fire four separate requests on one page.
 *
 * `roles` is the same CSV the endpoint accepted (e.g. "C1" or "B4,ADMIN");
 * blank/undefined returns everyone, matching the server's semantics.
 */
export function useAssignableUsers(
  roles?: string,
  options?: { enabled?: boolean },
) {
  const query = useQuery({
    queryKey: ["users", "assignable"],
    queryFn: () => usersApi.assignable(),
    staleTime: 10 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });

  const data = useMemo<AssignableUserDto[] | undefined>(() => {
    if (!query.data) return query.data;
    const wanted = (roles ?? "")
      .split(",")
      .map((r) => r.trim().toUpperCase())
      .filter(Boolean);
    if (wanted.length === 0) return query.data;
    return query.data.filter((u) =>
      u.roleCodes.some((rc) => wanted.includes(rc.toUpperCase())),
    );
  }, [query.data, roles]);

  return { ...query, data };
}
